import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarOff } from 'lucide-react';
import { ICalConflict } from '../../types/icalConflict';
import { formatDate } from '../../utils/formatDate';
import { NoAvailabilityGuidance } from './NoAvailabilityGuidance';

interface ConflictListItemProps {
  readonly conflict: ICalConflict;
  readonly onResolve: (conflict: ICalConflict) => void;
}

/**
 * One row in the ConflictsPanel (US1). Renders date range, source, and detection time for every
 * conflict, plus type-specific content:
 * - `DOUBLE_BOOKING`: the colliding booking reference (FR-002) and a "Resolve" action (US2/US3).
 * - `NO_AVAILABILITY`: explanatory guidance (FR-003) via `NoAvailabilityGuidance`, no action button
 *   at all (FR-007 — resolution happens implicitly via a later sync, never a manual dismiss here).
 */
export const ConflictListItem = ({ conflict, onResolve }: ConflictListItemProps) => {
  const { t } = useTranslation(['pages']);
  const isDoubleBooking = conflict.conflictType === 'DOUBLE_BOOKING';

  return (
    <div
      className={`card border-2 ${
        isDoubleBooking ? 'border-red-200 bg-red-50/40' : 'border-amber-200 bg-amber-50/40'
      }`}
      data-testid="conflict-list-item"
      data-conflict-type={conflict.conflictType}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            {isDoubleBooking ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                <AlertTriangle size={14} />
                {t('pages:conflictTypeDoubleBooking')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                <CalendarOff size={14} />
                {t('pages:conflictTypeNoAvailability')}
              </span>
            )}
            <span className="text-xs text-gray-500">{conflict.feedSourceLabel}</span>
          </div>

          <p className="font-medium text-gray-900">
            {formatDate(conflict.startDate)} → {formatDate(conflict.endDate)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {t('pages:conflictDetectedAt', { date: formatDate(conflict.detectedAt) })}
          </p>

          {isDoubleBooking ? (
            conflict.linkedBookingId && (
              <p className="text-sm text-gray-700 mt-2">
                {t('pages:conflictLinkedBooking', {
                  id: conflict.linkedBookingId.substring(0, 8),
                })}
              </p>
            )
          ) : (
            <NoAvailabilityGuidance
              apartmentId={conflict.apartmentId}
              startDate={conflict.startDate}
              endDate={conflict.endDate}
            />
          )}

          {conflict.status === 'RESOLVED' && conflict.resolutionOutcome && (
            <p className="text-xs text-gray-500 mt-2" data-testid="conflict-resolution-outcome">
              {t(
                conflict.resolutionOutcome === 'CANCEL_PLATFORM_BOOKING'
                  ? 'pages:conflictOutcomeCancelled'
                  : 'pages:conflictOutcomeDismissed'
              )}
            </p>
          )}
        </div>

        {isDoubleBooking && conflict.status === 'ACTIVE' && (
          <button
            type="button"
            onClick={() => onResolve(conflict)}
            className="btn-primary shrink-0"
          >
            {t('pages:resolveConflict')}
          </button>
        )}
      </div>
    </div>
  );
};
